import { inject, Injectable, InjectionToken } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import {
    ADDITIONAL_FILTERS_WITH_NO_VALUES_MESSAGE,
    DatasetFetchRuntimeParams,
    ExecutableRuntimeParameter,
    MetadataAndQueryURLsConfig,
    NamedQuery,
    NamedQueryInit,
    Query,
    RuntimeParameter,
} from '@ddv/models';
import { hashValue } from '@ddv/utils';
import { DropdownOption } from '@hs/ui-core-common';
import { catchError, delay, finalize, map, Observable, of, ReplaySubject, share, shareReplay, take, tap, throwError } from 'rxjs';

import { NamedQueryCloneRequest } from '../models/named-query-clone-request';
import { NamedQueryCloneResponse } from '../models/named-query-clone-response';

export const replaceHostInMetadataAndQueryURLsToken: InjectionToken<boolean> = new InjectionToken('replaceHostInMetadataAndQueryURLsToken');
export const trebekApiLocationToken: InjectionToken<string> = new InjectionToken('trebekApiLocationToken');
export const cfbApiLocationToken: InjectionToken<string> = new InjectionToken('cfbApiLocationToken');

@Injectable()
export class NamedQueriesService {
    private readonly currentStateService = inject(CurrentStateService);
    private readonly ddvApiExecutor = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly nqsApiExecutor = inject<ApiExecutorService>(ApiServices.nqs);
    private readonly replaceHostInURLs = inject(replaceHostInMetadataAndQueryURLsToken);
    private readonly trebekApiLocation = inject(trebekApiLocationToken);
    private readonly cfbApiLocation = inject(cfbApiLocationToken);

    private clientCode = '';
    private readonly namedQueriesCache = new Map<string, Observable<NamedQuery | undefined>>();
    private readonly executableQueriesCache = new Map<string, Query>();
    private readonly executableQueryRequestsCache = new Map<string, Observable<Query>>();
    private readonly config: MetadataAndQueryURLsConfig;

    constructor() {
        this.currentStateService.clientCode$
            .subscribe({
                next: (clientCode) => this.clientCode = clientCode,
            });

        this.config = {
            replaceHostInURLs: this.replaceHostInURLs,
            trebekApiLocation: this.trebekApiLocation,
            cfbApiLocation: this.cfbApiLocation,
        };
    }

    fetchNamedQueries(): Observable<NamedQuery[]> {
        return this.nqsApiExecutor.invokeServiceWithParams<NamedQueryInit[]>(this.clientCode, '/queries')
            .pipe(map((queries) => queries.map((q) => new NamedQuery(q, this.config))));
    }

    fetchNamedQuery(id: string, clientCode?: string): Observable<NamedQuery | undefined> {
        let namedQuery = this.namedQueriesCache.get(id);
        if (namedQuery) {
            return namedQuery;
        }

        namedQuery = this.nqsApiExecutor.invokeServiceWithParams<NamedQueryInit>(clientCode ?? this.clientCode, `/queries/${id}`)
            .pipe(
                share({
                    connector: () => new ReplaySubject(1),
                    resetOnError: false,
                    resetOnComplete: () => of(true).pipe(delay(30_000)), // caches the data for 30 seconds
                    resetOnRefCountZero: false,
                }),
                take(1),
                map((init) => new NamedQuery(init, this.config)),
                catchError(() => of(undefined)),
            );
        this.namedQueriesCache.set(id, namedQuery);

        return namedQuery;
    }

    // This method will always load no matter what and will not swallow errors
    loadNamedQuery(id: string, clientCode: string): Observable<NamedQuery> {
        return this.nqsApiExecutor.invokeServiceWithParams<NamedQueryInit>(clientCode, `/queries/${id}`)
            .pipe(
                map((init) => new NamedQuery(init, this.config)),
            );
    }

    fetchExecutableQuery(namedQuery: NamedQuery, parameterValues: DatasetFetchRuntimeParams): Observable<Query> {
        const runtimeParameters = this.getExecutableRuntimeParameters(namedQuery.parameters ?? [], parameterValues);
        const cacheKey = hashValue(`${namedQuery.id}-${JSON.stringify(runtimeParameters)}`);

        if (this.isAdditionalFilterWithNoValues(namedQuery.parameters ?? [], runtimeParameters)) {
            return throwError(() => new Error(ADDITIONAL_FILTERS_WITH_NO_VALUES_MESSAGE));
        }

        if (this.executableQueriesCache.has(cacheKey)) {
            return of(this.executableQueriesCache.get(cacheKey) as Query);
        }

        let executableQueryRequest = this.executableQueryRequestsCache.get(cacheKey);

        if (executableQueryRequest) {
            return executableQueryRequest;
        }

        const url = `/queries/${namedQuery.id}/executable-query`;

        executableQueryRequest = this.nqsApiExecutor.invokeServiceWithBody<Query>(this.clientCode, url, 'POST', { runtimeParameters })
            .pipe(
                shareReplay({ bufferSize: 1, refCount: true }),
                tap((query) => this.executableQueriesCache.set(cacheKey, query)),
                finalize(() => this.executableQueryRequestsCache.delete(cacheKey)),
            );
        this.executableQueryRequestsCache.set(cacheKey, executableQueryRequest);
        return executableQueryRequest;
    }

    cloneNamedQuery(request: NamedQueryCloneRequest): Observable<NamedQueryCloneResponse[]> {
        const { id, clientCode, clientCodes } = request;
        return this.ddvApiExecutor
            .invokeServiceWithBody<NamedQueryCloneResponse[]>(clientCode, `/admin/named-queries/${id}/clone`, 'POST', { clientCodes });
    }

    private getExecutableRuntimeParameters(
        parameters: RuntimeParameter[],
        parameterValues: DatasetFetchRuntimeParams,
    ): ExecutableRuntimeParameter[] {
        return parameters.reduce((runtimeParams: ExecutableRuntimeParameter[], param) => {
            if (param.name === 'startDate') {
                runtimeParams.push({ name: param.name, value: parameterValues.dateRange.from });
            }

            if (param.name === 'endDate' || param.name === 'asOfDate') {
                runtimeParams.push({ name: param.name, value: parameterValues.dateRange.to });
            }

            if (param.name === 'includeFunds') {
                runtimeParams.push({ name: param.name, value: parameterValues.fundCodes });
            }

            if (param.name === 'acknowledged' && parameterValues.specialCaseParams.acknowledged != null) {
                runtimeParams.push({ name: param.name, value: !!parameterValues.specialCaseParams.acknowledged });
            }

            if (param.name === 'includeManuallyReleased') {
                runtimeParams.push({ name: param.name, value: !!parameterValues.specialCaseParams.includeManuallyReleased });
            }

            if (param.type === 'additional-filter' && parameterValues.additionalFilters) {
                runtimeParams.push({
                    name: param.name,
                    value: this.getAdditionalFilterValues(parameterValues.additionalFilters, param.name),
                });
            }

            return runtimeParams;
        }, []);
    }

    private getAdditionalFilterValues(additionalFilters: Record<string, DropdownOption[]>[], paramName: string): string[] {
        return (additionalFilters.find((f) => Object.keys(f)[0] === paramName)?.[paramName] ?? []).map((f) => f.value);
    }

    private isAdditionalFilterWithNoValues(parameters: RuntimeParameter[], values: ExecutableRuntimeParameter[]): boolean {
        const additionalFilterParams = parameters.filter((p) => p.type === 'additional-filter');
        if (!additionalFilterParams.length) {
            return false;
        }
        return additionalFilterParams.some((p) => !values.some((v) => p.name === v.name && Array.isArray(v.value) && v.value.length > 0));
    }
}
